// What a page says of itself: its <head> tags, and the title a screen reader
// hears after a navigation.

import { element } from "./dom.ts";
import { headTags, type PageMeta } from "./meta.ts";

// What a screen reader hears after a navigation: the page's title, once it
// has settled (a page shows a placeholder title while it loads; setMeta
// restarts the wait each time it changes). `announcing` while a
// navigation's title is still to be said.
const announcer = element("#announcer", HTMLElement);
let announcing = false;
let announceTimer: ReturnType<typeof setTimeout> | undefined;

function announceSoon(): void {
  clearTimeout(announceTimer);
  announceTimer = setTimeout(() => {
    announcing = false;
    announcer.textContent = document.title;
  }, 500);
}

// Says the new page's title, once it settles: for a navigation.
export function announceTitle(): void {
  announcing = true;
  announceSoon();
}

// The <head> tags describing the current page (meta.ts's headTags).
// index.html ships the home page's (list and game pages arrive with their own
// already written in: see ../src/index.ts); every route replaces them, so a
// crawler that runs this script (Google does) sees each page's own title,
// description and canonical URL. `index: false` marks pages that answer 200
// but have nothing on them (the assets Worker can't send a real 404 for an
// unknown path).
export function setMeta(meta: PageMeta): void {
  const tags = headTags(meta);

  document.title = tags.title;
  if (announcing) announceSoon();
  for (const [attr, key, value] of tags.meta) metaTag(attr, key, value);

  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = tags.canonical;
}

// Sets <meta attr=key content=value>, adding it if missing; null removes it.
function metaTag(attr: string, key: string, value: string | null): void {
  let tag = document.querySelector(`meta[${attr}="${key}"]`);

  if (value == null) {
    tag?.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.append(tag);
  }
  tag.setAttribute("content", value);
}
